import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import {
    CRMFormData,
    CRMFormScopeData,
    CRMServerRequest,
    CRMServerResponse,
    CRMTeplateResponse,
    TemplateField
} from '../Types/Types'
import { FormProvider, UseFormMethods } from '../../../../../../Vendor/react-hook-form/dist/index.ie11'
import { CRMModalID } from '../Constants/Constants'
import DetailsTab from '../Views/DetailsTab'
import CLanguageHandler from '../../../../../../NonReact/Languages/LanguageHandler'
import LanguageContext from '../../../../../../React/Common/Contexts/LanguageContext'
import { CRMModal, LanguageErrorCodes } from '../../../../../../../i18n'
import { ModalUtilsContextType } from '../../../../../../React/Common/Contexts/ModalUtilsContext'
import { useModalUtilsContext } from '../../../../../../React/Common/Hooks/useModalUtilsContext'
import { Tab } from '../../../../../../React/Common/Components/Tabs/Types'
import { IconName } from '../../../../../../React/Common/Components/Icon'
import { HIDE } from '../../../../../../NonReact/constants/class-names'
import { EMPTY_STRING } from '../../../../../../React/Common/Constants/Separators'
import Tabs from '../../../../../../React/Common/Components/Tabs'
import { ConfigurationTab } from '../Views/ConfigurationTab'
import {
    CME_SCOPE_NAME,
    CRM_TYPE_SCOPE_NAME,
    GROUP_DEVICE_SCOPE_NAME,
    PARTITION_SCOPE_NAME,
    TELEPHONY_SERVER_SCOPE_NAME
} from '../Constants/FieldNames'
import { IsVarEmpty } from '../../../../../../NonReact/utils/GlobalFunctions'
import { mapFromStateToServer } from '../Mappers/Mappers'
import {
    addCRMConfiguration,
    changeCRMConfiguration,
    getCRMConfigTemplate
} from '../../../../../../NonReact/common/API/EntityAPIClient'
import { displayError } from '../../../../../../React/Common/Utils/Messages'
import { RoundedButton } from '../../../../Atoms/RoundedButton/RoundedButton'
import {
    ANY_DEVICE_VALUE,
    ANY_VALUE_STRING_DEFAULT,
    ANY_VALUE_STRING_NUMERIC
} from '../../../../../../React/Common/Constants/DefaultValues'
import { JSONResponse } from '../../../../../../NonReact/common/Types/Types'
import { getSession } from '../../../../../../React/Common/API/CommonAPI'
import { SessionAPIResponse } from '../../../../../../NonReact/Types/Types'
import { GenericScopeAssignmentTab } from '../../../../Molecules/GenericScopeTab/GenericScopeTab'
import { ReactState } from '../../../../../../React/Common/Types/Types'

type Props = {
    currentCRMConfiguration?: Nullable<CRMServerResponse>
    closeModal: (refresh: boolean) => void
    methods: UseFormMethods<CRMFormData>
    viewOnly?: boolean
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const CRMContent: FC<Props> = ({
    closeModal,
    methods,
    viewOnly,
    currentCRMConfiguration
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(LanguageErrorCodes.NAME)
    language.loadBundle(CRMModal.NAME)

    const [selectedCRMTemplate, setSelectedCRMTemplate]: ReactState<TemplateField[]> = useState<TemplateField[]>([])

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const { handleSubmit, watch }: UseFormMethods<CRMFormData> = methods

    const { tabID, setActiveTabID, validationErrors, invalidFormSubmit, isSubmitting }: ModalUtilsContextType =
        useModalUtilsContext()

    const crmType: string = watch(CRM_TYPE_SCOPE_NAME)

    const submitCRMConfiguration = async (state: CRMFormData): Promise<void> => {
        const isAdding: boolean = IsVarEmpty(currentCRMConfiguration)

        return getSession()
            .then((session: JSONResponse<SessionAPIResponse>): void => {
                const mappedPayload: CRMServerRequest = mapFromStateToServer(
                    state,
                    isAdding,
                    selectedCRMTemplate ?? [],
                    session
                )

                const { telephonyServer, partition, groupDevice }: CRMFormScopeData = state.scopeAssignment
                const device: string = groupDevice === ANY_VALUE_STRING_DEFAULT ? ANY_DEVICE_VALUE : groupDevice

                isAdding
                    ? addCRMConfiguration(Number(telephonyServer), Number(partition), device, mappedPayload)
                          .then((): void => closeModal(true))
                          .catch(displayError)
                    : changeCRMConfiguration(
                          Number(telephonyServer),
                          Number(partition),
                          device,
                          currentCRMConfiguration?.CRMConfigurationID ?? EMPTY_STRING,
                          mappedPayload
                      )
                          .then((): void => closeModal(true))
                          .catch(displayError)
            })
            .catch(displayError)
    }

    // List of all tabs.
    const tabs: Tab[] = [
        {
            ID: CRMModalID.CRMModalDetails,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(CRMModal.CRMDetailsTitle),
                iconName: IconName.PIN_NUM_OUTLINED,
                onClick: (): void => setActiveTabID(CRMModalID.CRMModalDetails)
            },
            content: <DetailsTab viewOnly={viewOnly} currentCRMConfiguration={currentCRMConfiguration} />,
            hasError: validationErrors[CRMModalID.CRMModalDetails]
        },
        {
            ID: CRMModalID.CRMScopeDetails,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(CRMModal.CRMScopeTitle),
                iconName: IconName.GPS_FIXED_OUTLINED,
                onClick: (): void => setActiveTabID(CRMModalID.CRMScopeDetails)
            },
            content: (
                <GenericScopeAssignmentTab
                    isViewOnly={viewOnly}
                    fieldNames={{
                        cmeId: CME_SCOPE_NAME,
                        telSysId: TELEPHONY_SERVER_SCOPE_NAME,
                        partitionId: PARTITION_SCOPE_NAME,
                        deviceId: GROUP_DEVICE_SCOPE_NAME
                    }}
                />
            ),
            hasError: validationErrors[CRMModalID.CRMScopeDetails]
        },
        {
            ID: CRMModalID.CRMConfigurationDetails,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(CRMModal.CRMConfigurationTitle),
                iconName: IconName.SETTINGS_CONFIG,
                onClick: (): void => setActiveTabID(CRMModalID.CRMConfigurationDetails)
            },
            content: (
                <ConfigurationTab
                    currentCRMConfiguration={currentCRMConfiguration}
                    selectedCRMTemplate={selectedCRMTemplate}
                    isViewOnly={viewOnly}
                />
            ),
            hasError: validationErrors[CRMModalID.CRMConfigurationDetails]
        }
    ]

    useEffect((): void => {
        if (crmType === ANY_VALUE_STRING_NUMERIC) return

        getCRMConfigTemplate(crmType)
            .then((response: JSONResponse<CRMTeplateResponse>): void => {
                if (isMounted.current) {
                    setSelectedCRMTemplate(response?.template ?? [])
                }
            })
            .catch(displayError)
    }, [crmType])

    return (
        <div className='crm-configuration-tabs'>
            <FormProvider {...methods}>
                <form
                    noValidate
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onSubmit={handleSubmit(submitCRMConfiguration, invalidFormSubmit)}
                    className={isSubmitting ? HIDE : EMPTY_STRING}
                >
                    {tabs && <Tabs tabs={tabs} activeTabID={tabID} />}
                    {!viewOnly ? (
                        <div className='footer-actions'>
                            <RoundedButton type='button' isOutlined onClick={(): void => closeModal(false)} shrink>
                                Cancel
                            </RoundedButton>
                            <RoundedButton type='submit' shrink>
                                Submit
                            </RoundedButton>
                        </div>
                    ) : (
                        <></>
                    )}
                </form>
            </FormProvider>
        </div>
    )
}
